import {
  Asset,
  FeatureName,
  IssueCreationSource,
  Severity,
  useIsFeatureEnabled,
  useStorageKeys,
} from '@gripp/shared-logic';
import {
  Button,
  Colors,
  SeverityIcons,
  TouchableButton,
  tokens,
} from '@gripp/shared-ui';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleProp, StyleSheet, View, ViewStyle } from 'react-native';
import { NewIssueModal, NewIssueModalProps } from '../issues';
import { localStorageHandler } from '../shared/localStorageHandler';

type ActionButtonsProps = {
  onCompleted: (message: string, messageDownloadApp: string) => void;
  asset: Asset;
  workspaceId: string;
  // CC layout only: triggers the parent's in-page report/note swap instead of a
  // modal. The legacy layout ignores this and opens NewIssueModal itself.
  onAddNew?: () => void;
  // Used only by the legacy (non-CC) layout. Kept for callers that still pass
  // them; ignored in the CC branch which manages its own sizing.
  buttonStyle?: StyleProp<ViewStyle>;
  titleFontSize?: number;
};

export const ActionButtons = (props: ActionButtonsProps) => {
  const isCropCircleRedesignEnabled = useIsFeatureEnabled(
    FeatureName.CropCircleRedesign
  );

  if (isCropCircleRedesignEnabled) {
    return <ActionButtonsCropCircle {...props} />;
  }

  return <ActionButtonsLegacy {...props} />;
};

const ActionButtonsCropCircle = (props: ActionButtonsProps) => {
  const { t } = useTranslation();

  return (
    // Full-width primary action floating at the bottom of the page.
    // The wrapper's height pins this flex child so it doesn't collapse
    // to 0 on web inside the BottomComponentBar's flex-column wrapper.
    // The form itself is rendered in-page by the parent via onAddNew (no modal).
    <View style={ccStyles.actionWrapper}>
      <Button style={ccStyles.button} onPress={props.onAddNew} state="active">
        {`+ ${t('asset.header.buttons.addNew')}`}
      </Button>
    </View>
  );
};

const ActionButtonsLegacy = (props: ActionButtonsProps) => {
  const { t } = useTranslation();
  const storageKeys = useStorageKeys();
  const [issueModalVisible, setIssueModalVisible] = useState(false);
  const [newIssueModalProps, setNewIssueModalProps] = useState<
    NewIssueModalProps | undefined
  >(undefined);

  const trackIssueId = async (issueId: string) => {
    const existing = await localStorageHandler.getItem(
      storageKeys.anonymousIssueIds
    );
    const ids: string[] = existing ? JSON.parse(existing) : [];
    if (!ids.includes(issueId)) {
      ids.push(issueId);
    }
    await localStorageHandler.setItem(
      storageKeys.anonymousIssueIds,
      JSON.stringify(ids)
    );
  };

  // Await the storage write before notifying the parent so the OneLink view
  // sees the newly created issue id. The modal's onComplete then just closes
  // the modal — keeping the success copy + storage read in a single async path.
  const makeOnIssueCreated =
    (message: string, messageDownloadApp: string) =>
    async (issueId: string) => {
      await trackIssueId(issueId);
      props.onCompleted(message, messageDownloadApp);
    };

  const onReportIssue = () => {
    setNewIssueModalProps({
      assetId: props.asset.id,
      workspaceId: props.workspaceId!,
      isOpened: issueModalVisible,
      modalType: 'modal',
      onComplete: () => setIssueModalVisible(false),
      onIssueCreated: makeOnIssueCreated(
        t('issue.anonymous.reported'),
        t('issue.anonymous.reportAddedDownloadGrippApp')
      ),
      onCancel: onCancel,
      createdFrom: IssueCreationSource.Anonymousweb,
      pickerMode: 'picker',
    });
    setIssueModalVisible(true);
  };

  const onNewNote = () => {
    setNewIssueModalProps({
      assetId: props.asset.id,
      workspaceId: props.workspaceId!,
      isOpened: issueModalVisible,
      modalType: 'modal',
      onComplete: () => setIssueModalVisible(false),
      onIssueCreated: makeOnIssueCreated(
        t('issue.anonymous.noteAdded'),
        t('issue.anonymous.noteAddedDownloadGrippApp')
      ),
      onCancel: onCancel,
      createdFrom: IssueCreationSource.Anonymousweb,
      pickerMode: 'picker',
    });
    setIssueModalVisible(true);
  };

  const onCancel = () => {
    setIssueModalVisible(false);
  };

  return (
    <>
      <View style={legacyStyles.container}>
        <TouchableButton
          color={Colors.white}
          icon={SeverityIcons[Severity.Red]}
          label={t('issue.anonymous.createReport')}
          style={[
            legacyStyles.button,
            legacyStyles.buttonText,
            legacyStyles.report,
            props.buttonStyle,
          ]}
          onPress={onReportIssue}
          titleFontSize={props.titleFontSize}
        />
        <TouchableButton
          color={Colors.white}
          icon={SeverityIcons[Severity.Purple]}
          label={t('issue.anonymous.addNote')}
          style={[
            legacyStyles.button,
            legacyStyles.buttonText,
            legacyStyles.note,
            props.buttonStyle,
          ]}
          onPress={onNewNote}
          titleFontSize={props.titleFontSize}
        />
      </View>
      {newIssueModalProps && (
        <NewIssueModal
          {...newIssueModalProps}
          isOpened={issueModalVisible}
          hideAdvancedSettings
        />
      )}
    </>
  );
};

const ccStyles = StyleSheet.create({
  actionWrapper: {
    flexGrow: 1,
    height: 56,
    marginLeft: tokens.spacing.lg,
    flexDirection: 'row',
  },
  button: {
    flex: 1,
    height: 56,
    maxHeight: 56,
    borderRadius: tokens.borderRadius.xs,
    backgroundColor: tokens.colors.orange[500],
  },
});

const legacyStyles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    gap: 8,
    paddingLeft: 20,
  },
  button: {
    maxWidth: 167,
    height: 81,
    borderRadius: 8,
    marginBottom: 20,
  },
  buttonText: {
    flex: 1,
    flexDirection: 'column',
    lineHeight: 18,
    fontWeight: '500',
    justifyContent: 'center',
    alignItems: 'center',
  },
  report: {
    backgroundColor: Colors.red,
  },
  note: {
    backgroundColor: Colors.purple,
  },
});
